import { useSyncExternalStore } from 'react';

/**
 * Viewport breakpoints for the Rocky main app and Eval.
 *
 *  - mobile   ≤ 767px   — phones. Sidebar becomes a drawer behind a top bar, chat
 *                         takes the whole screen when open.
 *  - tablet   768–1199  — iPads, portrait and landscape. The icon rail stays, chat
 *                         slides OVER the content instead of taking a column.
 *  - desktop  ≥ 1200    — unchanged from the desktop-only layout.
 *
 * ⚠️ The CSS repeats these numbers as `@media (max-width: 767px)` and
 * `@media (max-width: 1199px)` — custom properties cannot be used inside a media
 * query. Change one side and you change the other, or JS and CSS will disagree about
 * which layout is on screen (e.g. JS thinks the chat is an overlay while CSS still
 * lays it out as a column).
 *
 * Scope: IVT and Event Hub are NOT responsive and must not consume this — their
 * layouts are owned by other teams and were explicitly left desktop-only.
 */
export type Breakpoint = 'mobile' | 'tablet' | 'desktop';

export const MOBILE_MAX_WIDTH = 767;
export const TABLET_MAX_WIDTH = 1199;

const MOBILE_QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px)`;
const COMPACT_QUERY = `(max-width: ${TABLET_MAX_WIDTH}px)`;

/** null where matchMedia does not exist — jsdom, which every component test runs in. */
function mediaList(query: string): MediaQueryList | null {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(query)
    : null;
}

/**
 * The breakpoint right now. Falls back to desktop without matchMedia, so tests that
 * never opted into a viewport keep the layout they were written against.
 */
export function currentBreakpoint(): Breakpoint {
  if (mediaList(MOBILE_QUERY)?.matches) return 'mobile';
  if (mediaList(COMPACT_QUERY)?.matches) return 'tablet';
  return 'desktop';
}

function subscribe(onChange: () => void): () => void {
  const lists = [mediaList(MOBILE_QUERY), mediaList(COMPACT_QUERY)].filter(
    (l): l is MediaQueryList => l !== null,
  );
  lists.forEach((l) => l.addEventListener('change', onChange));
  return () => lists.forEach((l) => l.removeEventListener('change', onChange));
}

/**
 * The current breakpoint, re-rendering when the viewport crosses one (rotating an
 * iPad, resizing a desktop window). useSyncExternalStore rather than useState +
 * effect so the first render already has the right value — no desktop-layout flash
 * on a phone before the effect corrects it.
 */
export function useBreakpoint(): Breakpoint {
  return useSyncExternalStore(subscribe, currentBreakpoint, () => 'desktop');
}

/** True below desktop — the layouts where chat overlays rather than takes a column. */
export function isCompact(bp: Breakpoint): boolean {
  return bp !== 'desktop';
}
